import React, { useCallback, useEffect, useState } from 'react';
import { Tag, Alert, Spin, Button } from 'antd';
import {
  MobileOutlined, WarningOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import mobileService from '../../../../services/mobileService';
import { formatDate, TYPES_DOMMAGE } from '../../../../variables/constants';
import CarteGps from './CarteGps';
import PiecesMobiles from './PiecesMobiles';

/**
 * Onglet « Déclaration mobile » du detail sinistre.
 *
 * AUTONOME : il va chercher ses donnees sur `/v1/mobile-backoffice/declarations/{id}`
 * et ne lit rien du `sinistre` passe par la page. C'est necessaire - `SinistreResponse`
 * n'expose ni `canal`, ni GPS, ni usager declarant : ces informations n'existent que
 * dans le DTO du module mobile.
 *
 * L'endpoint n'est PAS restreint au statut SOUMISE, seulement au canal MOBILE : les
 * donnees de declaration restent donc consultables longtemps apres la validation,
 * ce qui est precisement l'interet de cet onglet.
 *
 * Onglet a part, et non lignes ajoutees a « Informations » : ces donnees n'existent
 * que pour une fraction des dossiers, et les diluer dans la fiche generale obligerait
 * chaque agent a les chercher sur les milliers de dossiers WEB ou elles sont vides.
 *
 * ── CE QUE L'AGENT VIENT Y CHERCHER, ET CE QUE LA MISE EN PAGE EN DIT ──
 *
 * Deux questions, dans cet ordre : cette declaration est-elle digne de foi, et
 * qu'est-ce que la personne a reellement envoye ? L'ecran suit donc deux sections,
 * et non trois blocs de meme poids separes par un filet.
 *
 * LE COMPTE MOBILE PASSE AVANT LE DECLARANT SAISI. L'un est une identite
 * authentifiee par l'application, l'autre ce que quelqu'un a tape dans un
 * formulaire. Les presenter dans l'autre sens - ce que faisait cet ecran -
 * revenait a donner le premier role a la donnee la moins sure, et la phrase qui
 * expliquait la difference finissait en note grise de 11,5 px sous le tableau.
 * Elle est maintenant dans les libelles eux-memes, la ou se porte le regard.
 *
 * L'ECART D'IDENTITE N'EST PLUS UN SECOND BANDEAU JAUNE. Deux alertes de meme
 * couleur empilees se neutralisent ; celle-ci se lit sous les deux noms qu'elle
 * compare, puisque c'est la qu'elle a un sens.
 *
 * La position tient sur une ligne tant qu'on ne deplie pas la carte (voir
 * `CarteGps`, qui ne charge rien avant le clic) : elle n'a pas besoin d'une
 * section a elle.
 */

const GREEN = '#1a6b3c';
const INK = '#111827';
const INK_MUTED = '#6b7280';
const INK_LIGHT = '#9ca3af';
const WARN = '#b54708';

const BANDE = {
  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
  padding: '12px 16px', marginBottom: 16, borderRadius: 12,
  background: '#f8fafc', border: '1px solid #e5e7eb',
};

const libelleDommage = (v) => TYPES_DOMMAGE.find((o) => o.value === v)?.label || v || '-';
const nomComplet = (prenom, nom) => [prenom, nom].filter(Boolean).join(' ');

/** Titre de section : sobre, sans filet ni icone - les sections sont deja des cartes. */
function Section({ titre, children, style }) {
  return (
    <section className="cb-form-section" style={{ marginBottom: 16, ...style }}>
      <h3 style={{
        margin: '0 0 14px', fontSize: 14, fontWeight: 600, color: GREEN,
      }}>
        {titre}
      </h3>
      {children}
    </section>
  );
}

/**
 * Une donnee et son libelle, cote a cote tant qu'il y a la place, l'un sous
 * l'autre sinon.
 *
 * EN FLEX ET NON EN GRILLE : une grille a premiere colonne figee ne se replie
 * pas sans requete de media, et les styles sont ici en ligne. Le libelle garde
 * une base de 11 rem sans pouvoir s'etirer, la valeur reclame au moins 16 rem :
 * sous environ 27 rem de large, elle passe donc a la ligne d'elle-meme. C'est ce
 * que `Descriptions` d'AntD, avec sa largeur de libelle figee a 160 px, ne
 * savait pas faire dans le volet etroit du dossier.
 */
function Ligne({ libelle, precision, children }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'baseline',
      columnGap: 16, rowGap: 2, padding: '7px 0', borderTop: '1px solid #f1f3f6',
    }}>
      <div style={{
        flex: '0 1 11rem', fontSize: 12.5, color: INK_MUTED, lineHeight: 1.45,
      }}>
        {libelle}
        {precision && (
          <span style={{ display: 'block', fontSize: 11, color: INK_LIGHT }}>{precision}</span>
        )}
      </div>
      <div style={{
        flex: '1 1 16rem', minWidth: 0, fontSize: 13, color: INK, lineHeight: 1.45,
      }}>
        {children}
      </div>
    </div>
  );
}

const Vide = () => <span style={{ color: INK_LIGHT }}>-</span>;
const val = (v) => (v || <Vide />);

/** Nom et telephone sur la meme ligne : on appelle la personne, pas son nom seul. */
function Personne({ nom, telephone, apres, t }) {
  if (!nom && !telephone) return <Vide />;
  return (
    <>
      <span style={{ fontWeight: 600 }}>{val(nom)}</span>
      <span style={{ color: telephone ? INK_MUTED : INK_LIGHT, marginLeft: 10, fontSize: 12.5 }}>
        {telephone || t('ongletDeclarationMobile.sansTelephone')}
      </span>
      {apres}
    </>
  );
}

export default function OngletDeclarationMobile({ sinistreTrackingId, statut }) {
  const { t } = useTranslation('dossierSinistre');
  const [declaration, setDeclaration] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    if (!sinistreTrackingId) return;
    setChargement(true);
    setErreur(null);
    try {
      setDeclaration(await mobileService.detailDeclaration(sinistreTrackingId));
    } catch (err) {
      setErreur(err?.response?.data?.message
        || i18n.t('dossierSinistre:ongletDeclarationMobile.donneesIndisponibles'));
      setDeclaration(null);
    } finally {
      setChargement(false);
    }
  }, [sinistreTrackingId]);

  useEffect(() => { charger(); }, [charger]);

  if (chargement) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  }

  if (erreur) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          showIcon
          message={erreur}
          action={<Button size="small" icon={<ReloadOutlined />} onClick={charger}>{t('declarationMobile.reessayer')}</Button>}
        />
      </div>
    );
  }

  if (!declaration) return null;

  const declarant = nomComplet(declaration.declarantPrenom, declaration.declarantNom);
  const usager = nomComplet(declaration.usagerPrenom, declaration.usagerNom);
  const ecartIdentite = !!declarant && !!usager
    && declarant.trim().toLowerCase() !== usager.trim().toLowerCase();
  const lieu = [declaration.lieuAccident, declaration.commune, declaration.ville]
    .filter(Boolean).join(' - ');

  return (
    <div style={{ padding: 24 }}>
      {/* Ce que la declaration est, en une ligne : meme forme que la bande de
          l'assure sur l'onglet Victimes, pour que les deux ecrans se lisent pareil. */}
      <div style={BANDE}>
        <Tag color="green" style={{ borderRadius: 20, margin: 0, fontWeight: 600 }}>
          <MobileOutlined /> {t('ongletDeclarationMobile.tagCanal')}
        </Tag>
        <span style={{ fontSize: 12.5, color: INK_MUTED }}>
          {t('ongletDeclarationMobile.declareeLe')}&nbsp;
          <strong style={{ color: INK }}>
            {declaration.dateDeclaration ? formatDate(declaration.dateDeclaration) : '-'}
          </strong>
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12.5, color: INK_MUTED }}>
          {t('ongletDeclarationMobile.dommagesDeclares')}&nbsp;
          <strong style={{ color: INK }}>{libelleDommage(declaration.typeDommage)}</strong>
        </span>
      </div>

      {statut === 'SOUMISE' && (
        <Alert
          type="warning"
          showIcon
          icon={<MobileOutlined />}
          message={t('ongletDeclarationMobile.nonValidee')}
          description={t('ongletDeclarationMobile.nonValideeDetail')}
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}

      <Section titre={t('ongletDeclarationMobile.quiADeclare')}>
        {/* L'identite authentifiee d'abord : c'est la seule que l'application garantit. */}
        <Ligne
          libelle={t('declarationMobile.compteMobile')}
          precision={t('ongletDeclarationMobile.identiteAuthentifiee')}
        >
          <Personne nom={usager} telephone={declaration.usagerTelephone} t={t} />
        </Ligne>

        <Ligne
          libelle={t('declarationMobile.declarantSaisi')}
          precision={t('ongletDeclarationMobile.nonVerifie')}
        >
          <Personne
            nom={declarant}
            telephone={declaration.declarantTelephone}
            t={t}
            apres={declaration.declarantQualite && (
              <Tag style={{ marginLeft: 8, borderRadius: 12 }}>{declaration.declarantQualite}</Tag>
            )}
          />
          {ecartIdentite && (
            <div style={{
              marginTop: 6, fontSize: 12, color: WARN, lineHeight: 1.5,
              display: 'flex', gap: 6, alignItems: 'flex-start',
            }}>
              <WarningOutlined style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{t('ongletDeclarationMobile.ecartIdentite')}</span>
            </div>
          )}
        </Ligne>

        <Ligne libelle={t('ongletDeclarationMobile.position')}>
          <CarteGps
            latitude={declaration.latitude}
            longitude={declaration.longitude}
            hauteur={280}
          />
        </Ligne>

        {lieu && (
          <Ligne libelle={t('ongletDeclarationMobile.lieu')}>{lieu}</Ligne>
        )}
      </Section>

      <Section titre={t('ongletDeclarationMobile.ceQuiAEteTransmis')} style={{ marginBottom: 0 }}>
        <PiecesMobiles
          sinistreTrackingId={sinistreTrackingId}
          titre={t('ongletDeclarationMobile.piecesDeposees')}
        />
      </Section>
    </div>
  );
}
